// CRM Lead Management — admin-only module.
//
// Mounted as a tab inside AdminDashboard at /admin?tab=crm. Surface:
//   • Dashboard: 10 summary counter cards
//   • Leads:     searchable + filterable table; Add / Edit / Delete /
//                Mark Complete / WhatsApp / Call / View detail (timeline)
//   • Follow-ups (Today / Overdue / Upcoming) — coming in iter 2.
//   • Export CSV button — opens the backend CSV stream.
//
// Backend endpoints live under /api/admin/crm/* (see backend/crm.py).
// Every call uses the admin's bearer token from axios defaults — no
// extra auth wiring needed here.

import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus, RefreshCw, Search, Pencil, Trash2, Eye, Phone, MessageCircle,
  CheckCircle2, Filter, Download, X, ListTree, ArrowDownAZ,
  Briefcase, Flame, BellRing, Sparkles, GraduationCap, Smartphone,
  Inbox as InboxIcon, ShieldCheck, ChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WHATSAPP_TEMPLATE = "Namaste! 🙏 This is MDM Sahayak Support. We received your enquiry — how can we help you today? You can ask about UC generation, Hand Receipts, Quarterly bills, payment activation, or Teacher Toolkit. Reply here and we will guide you step by step.";

// =================================================================
// SUMMARY CARDS GRID
// =================================================================
const CARD_META = [
  { key: "totalLeads", label: "Total Leads", icon: ListTree, tone: "from-clay/15 to-clay/5 text-clay-dark" },
  { key: "newLeads", label: "New Leads", icon: Sparkles, tone: "from-amber-200 to-amber-50 text-amber-800" },
  { key: "positiveLeads", label: "Positive", icon: CheckCircle2, tone: "from-emerald-200 to-emerald-50 text-emerald-800" },
  { key: "negativeLeads", label: "Negative", icon: X, tone: "from-rose-200 to-rose-50 text-rose-800" },
  { key: "pendingFollowUps", label: "Pending Follow-Ups", icon: BellRing, tone: "from-amber-200 to-orange-50 text-orange-800" },
  { key: "completedWorks", label: "Completed", icon: ShieldCheck, tone: "from-emerald-200 to-teal-50 text-teal-800" },
  { key: "highPriorityLeads", label: "High Priority", icon: Flame, tone: "from-rose-200 to-pink-50 text-rose-800" },
  { key: "todayFollowUps", label: "Today's Follow-Ups", icon: BellRing, tone: "from-clay/20 to-amber-50 text-clay-dark" },
  { key: "mdmEnquiries", label: "MDM Enquiries", icon: Briefcase, tone: "from-sky-200 to-sky-50 text-sky-800" },
  { key: "toolkitEnquiries", label: "Teacher Toolkit", icon: GraduationCap, tone: "from-violet-200 to-violet-50 text-violet-800" },
];

function SummaryGrid({ summary }) {
  return (
    <div className="grid grid-cols-2 gap-2 sm:gap-3 md:grid-cols-3 lg:grid-cols-5" data-testid="crm-summary-grid">
      {CARD_META.map(({ key, label, icon: Icon, tone }) => (
        <div
          key={key}
          data-testid={`crm-summary-${key}`}
          className={`rounded-2xl border border-border bg-gradient-to-br ${tone} px-3 py-3 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md`}
        >
          <div className="flex items-center justify-between">
            <p className="text-[0.6rem] font-bold uppercase tracking-wide leading-tight">{label}</p>
            <Icon className="h-3.5 w-3.5 opacity-80" />
          </div>
          <p className="mt-1 font-heading text-2xl font-extrabold leading-none">
            {summary?.[key] ?? "—"}
          </p>
        </div>
      ))}
    </div>
  );
}

// =================================================================
// PRIORITY / STATUS BADGES (colours per spec — red/orange/green etc)
// =================================================================
const STATUS_TONE = {
  "New Lead": "bg-clay/10 text-clay-dark",
  "Interested": "bg-sky-100 text-sky-800",
  "Positive": "bg-emerald-100 text-emerald-800",
  "Negative": "bg-rose-100 text-rose-700",
  "No Response": "bg-zinc-100 text-zinc-700",
  "Follow Up Required": "bg-amber-100 text-amber-800",
  "Payment Pending": "bg-orange-100 text-orange-800",
  "Activated": "bg-violet-100 text-violet-800",
  "Completed": "bg-teal-100 text-teal-800",
  "Closed": "bg-zinc-200 text-zinc-700",
};
const PRIORITY_TONE = {
  "High Priority": "bg-rose-600 text-white",
  "Medium Priority": "bg-orange-500 text-white",
  "Low Priority": "bg-emerald-600 text-white",
};

function Badge({ children, tone, testId }) {
  return <span data-testid={testId} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[0.6rem] font-semibold ${tone}`}>{children}</span>;
}

// =================================================================
// LEAD FORM (modal) — used for both Add and Edit
// =================================================================
const EMPTY_LEAD = {
  fullName: "", mobileNumber: "", altNumber: "", whatsappNumber: "",
  district: "", block: "", schoolName: "", udiseCode: "",
  enquiryType: "MDM Sahayak", matter: "", leadSource: "WhatsApp",
  leadStatus: "New Lead", priority: "Medium Priority", tags: [],
  followUpRequired: false, followUpDate: "", followUpTime: "",
  workProgress: "Not Started", internalNote: "",
};

function LeadFormDialog({ open, mode, initial, catalog, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY_LEAD);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(mode === "edit" && initial ? { ...EMPTY_LEAD, ...initial } : EMPTY_LEAD);
  }, [open, mode, initial]);

  const upd = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleTag = (t) => setForm((f) => ({
    ...f,
    tags: f.tags.includes(t) ? f.tags.filter((x) => x !== t) : [...f.tags, t],
  }));

  const save = async () => {
    if (!form.fullName.trim()) { toast.error("Full Name is required"); return; }
    if (!form.mobileNumber.trim()) { toast.error("Mobile Number is required"); return; }
    setSaving(true);
    try {
      if (mode === "edit" && initial?.id) {
        await axios.put(`${API}/admin/crm/leads/${initial.id}`, form);
        toast.success("Lead updated");
      } else {
        await axios.post(`${API}/admin/crm/leads`, form);
        toast.success("Lead added");
      }
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save lead");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto" data-testid="crm-lead-form-dialog">
        <DialogHeader>
          <DialogTitle>{mode === "edit" ? "Edit Lead" : "Add New Lead"}</DialogTitle>
          <DialogDescription>Capture every detail — required fields marked with ★.</DialogDescription>
        </DialogHeader>

        {/* Section: basic info */}
        <p className="text-[0.65rem] font-bold uppercase tracking-wide text-clay">Basic Information</p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Full Name ★">
            <Input value={form.fullName} onChange={(e) => upd("fullName")(e.target.value)} data-testid="crm-form-fullName" />
          </Field>
          <Field label="Mobile Number ★">
            <Input value={form.mobileNumber} onChange={(e) => upd("mobileNumber")(e.target.value)} data-testid="crm-form-mobileNumber" />
          </Field>
          <Field label="Alternate Number">
            <Input value={form.altNumber} onChange={(e) => upd("altNumber")(e.target.value)} data-testid="crm-form-altNumber" />
          </Field>
          <Field label="WhatsApp Number">
            <Input value={form.whatsappNumber} onChange={(e) => upd("whatsappNumber")(e.target.value)} data-testid="crm-form-whatsappNumber" />
          </Field>
          <Field label="District">
            <Input value={form.district} onChange={(e) => upd("district")(e.target.value)} data-testid="crm-form-district" />
          </Field>
          <Field label="Block">
            <Input value={form.block} onChange={(e) => upd("block")(e.target.value)} data-testid="crm-form-block" />
          </Field>
          <Field label="School Name">
            <Input value={form.schoolName} onChange={(e) => upd("schoolName")(e.target.value)} data-testid="crm-form-schoolName" />
          </Field>
          <Field label="UDISE Code (optional)">
            <Input value={form.udiseCode} onChange={(e) => upd("udiseCode")(e.target.value)} data-testid="crm-form-udise" />
          </Field>
        </div>

        {/* Section: enquiry */}
        <p className="mt-3 text-[0.65rem] font-bold uppercase tracking-wide text-clay">Enquiry</p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Enquiry Type">
            <SimpleSelect testId="crm-form-enquiryType" value={form.enquiryType} options={catalog?.enquiryTypes || []} onChange={upd("enquiryType")} />
          </Field>
          <Field label="Lead Source">
            <SimpleSelect testId="crm-form-leadSource" value={form.leadSource} options={catalog?.leadSources || []} onChange={upd("leadSource")} />
          </Field>
          <div className="sm:col-span-2">
            <FieldLabel>Matter / Requirement</FieldLabel>
            <Textarea rows={3} value={form.matter} onChange={(e) => upd("matter")(e.target.value)} data-testid="crm-form-matter" />
          </div>
        </div>

        {/* Section: status & priority */}
        <p className="mt-3 text-[0.65rem] font-bold uppercase tracking-wide text-clay">Status &amp; Priority</p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Field label="Lead Status">
            <SimpleSelect testId="crm-form-leadStatus" value={form.leadStatus} options={catalog?.leadStatuses || []} onChange={upd("leadStatus")} />
          </Field>
          <Field label="Priority">
            <SimpleSelect testId="crm-form-priority" value={form.priority} options={catalog?.priorities || []} onChange={upd("priority")} />
          </Field>
          <Field label="Work Progress">
            <SimpleSelect testId="crm-form-workProgress" value={form.workProgress} options={catalog?.workProgress || []} onChange={upd("workProgress")} />
          </Field>
        </div>

        {/* Tags */}
        <div className="mt-1">
          <FieldLabel>Tags / Labels (multi-select)</FieldLabel>
          <div className="flex flex-wrap gap-1.5" data-testid="crm-form-tags">
            {(catalog?.tags || []).map((t) => {
              const on = form.tags.includes(t);
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => toggleTag(t)}
                  data-testid={`crm-form-tag-${t.replace(/\s+/g, "-").toLowerCase()}`}
                  className={`rounded-full border px-2 py-0.5 text-[0.65rem] font-semibold transition ${on ? "border-clay bg-clay text-white" : "border-border bg-white text-ink-700 hover:bg-sand-100"}`}
                >
                  {t}
                </button>
              );
            })}
          </div>
        </div>

        {/* Follow-up */}
        <p className="mt-3 text-[0.65rem] font-bold uppercase tracking-wide text-clay">Follow-Up</p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div>
            <FieldLabel>Required?</FieldLabel>
            <button
              type="button"
              onClick={() => upd("followUpRequired")(!form.followUpRequired)}
              data-testid="crm-form-followUpRequired"
              className={`h-8 w-full rounded-md border text-xs font-semibold transition ${form.followUpRequired ? "border-emerald-500 bg-emerald-50 text-emerald-800" : "border-border bg-white text-ink-700"}`}
            >
              {form.followUpRequired ? "Yes" : "No"}
            </button>
          </div>
          <Field label="Date">
            <Input type="date" value={form.followUpDate || ""} onChange={(e) => upd("followUpDate")(e.target.value)} data-testid="crm-form-followUpDate" />
          </Field>
          <Field label="Time">
            <Input type="time" value={form.followUpTime || ""} onChange={(e) => upd("followUpTime")(e.target.value)} data-testid="crm-form-followUpTime" />
          </Field>
        </div>

        <div className="mt-3">
          <FieldLabel>Internal Admin Note (private)</FieldLabel>
          <Textarea rows={4} value={form.internalNote} onChange={(e) => upd("internalNote")(e.target.value)} data-testid="crm-form-internalNote" />
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" size="sm" onClick={onClose} data-testid="crm-form-cancel"><X className="mr-1 h-3.5 w-3.5" /> Cancel</Button>
          <Button size="sm" onClick={save} disabled={saving} className="bg-clay text-white hover:bg-clay-dark" data-testid="crm-form-save">
            {saving ? "Saving…" : (mode === "edit" ? "Save changes" : "Add Lead")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FieldLabel({ children }) { return <label className="mb-1 block text-[0.6rem] font-semibold uppercase tracking-wide text-ink-700">{children}</label>; }
function Field({ label, children }) { return <div><FieldLabel>{label}</FieldLabel>{children}</div>; }
function SimpleSelect({ value, options, onChange, testId }) {
  // Radix Select forbids empty-string SelectItem values, so we map a literal
  // empty string (meaning "no filter") to a sentinel "__all" internally.
  const ALL = "__all";
  return (
    <Select value={value || ALL} onValueChange={(v) => onChange(v === ALL ? "" : v)}>
      <SelectTrigger className="h-8 text-xs" data-testid={testId}><SelectValue /></SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          o === "" || o == null
            ? <SelectItem key={ALL} value={ALL}>All</SelectItem>
            : <SelectItem key={o} value={o}>{o}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// =================================================================
// LEAD DETAIL DIALOG — read-only timeline + actions
// =================================================================
function LeadDetailDialog({ open, leadId, onClose, onChanged, onEdit }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [savingNote, setSavingNote] = useState(false);

  const reload = async () => {
    if (!leadId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/crm/leads/${leadId}`);
      setData(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load lead"); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (open) { reload(); setNoteText(""); } }, [open, leadId]);

  if (!open) return null;
  const lead = data?.lead;

  const addNote = async () => {
    if (!noteText.trim()) return;
    setSavingNote(true);
    try {
      await axios.post(`${API}/admin/crm/leads/${leadId}/notes`, { body: noteText.trim() });
      setNoteText("");
      await reload();
      toast.success("Note added");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add note"); }
    finally { setSavingNote(false); }
  };

  const markComplete = async () => {
    try {
      await axios.post(`${API}/admin/crm/leads/${leadId}/complete`);
      toast.success("Marked completed");
      await reload();
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not complete"); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto" data-testid="crm-lead-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <InboxIcon className="h-4 w-4 text-clay" /> Lead Detail
          </DialogTitle>
          <DialogDescription>
            {loading ? "Loading…" : lead ? `${lead.fullName} · ${lead.mobileNumber}` : "—"}
          </DialogDescription>
        </DialogHeader>

        {lead && (
          <div className="space-y-3 text-xs">
            {/* Action toolbar */}
            <div className="flex flex-wrap gap-1.5" data-testid="crm-detail-actions">
              <Button size="sm" variant="outline" onClick={() => onEdit?.(lead)} className="h-7 text-xs" data-testid="crm-detail-edit"><Pencil className="mr-1 h-3 w-3" /> Edit</Button>
              <Button size="sm" variant="outline" onClick={markComplete} className="h-7 border-emerald-400 text-xs text-emerald-700 hover:bg-emerald-50" data-testid="crm-detail-complete"><CheckCircle2 className="mr-1 h-3 w-3" /> Mark Completed</Button>
              <a href={whatsappUrl(lead)} target="_blank" rel="noreferrer">
                <Button size="sm" variant="outline" className="h-7 border-emerald-400 text-xs text-emerald-700 hover:bg-emerald-50" data-testid="crm-detail-whatsapp"><MessageCircle className="mr-1 h-3 w-3" /> WhatsApp</Button>
              </a>
              <a href={`tel:+91${(lead.mobileNumber || "").replace(/\D/g, "")}`}>
                <Button size="sm" variant="outline" className="h-7 text-xs" data-testid="crm-detail-call"><Phone className="mr-1 h-3 w-3" /> Call</Button>
              </a>
            </div>

            {/* Summary card */}
            <div className="grid grid-cols-2 gap-2 rounded-lg border border-border bg-white p-3">
              <Info label="Enquiry" value={lead.enquiryType} />
              <Info label="Source" value={lead.leadSource} />
              <Info label="Status" value={<Badge tone={STATUS_TONE[lead.leadStatus]}>{lead.leadStatus}</Badge>} />
              <Info label="Priority" value={<Badge tone={PRIORITY_TONE[lead.priority]}>{lead.priority}</Badge>} />
              <Info label="Work Progress" value={lead.workProgress} />
              <Info label="District" value={`${lead.district || "—"} / ${lead.block || "—"}`} />
              <Info label="School" value={`${lead.schoolName || "—"} ${lead.udiseCode ? `(${lead.udiseCode})` : ""}`} />
              <Info label="Follow-Up" value={lead.followUpRequired ? `${lead.followUpDate} ${lead.followUpTime || ""}` : "—"} />
              <div className="col-span-2">
                <FieldLabel>Matter</FieldLabel>
                <p className="rounded bg-sand-50 px-2 py-1.5 text-[0.75rem] text-ink-900">{lead.matter || "—"}</p>
              </div>
              {lead.tags?.length > 0 && (
                <div className="col-span-2 flex flex-wrap gap-1">
                  {lead.tags.map((t) => <Badge key={t} tone="bg-clay/15 text-clay-dark">{t}</Badge>)}
                </div>
              )}
              {lead.internalNote && (
                <div className="col-span-2">
                  <FieldLabel>Internal Admin Note</FieldLabel>
                  <p className="rounded bg-amber-50 px-2 py-1.5 text-[0.75rem] text-amber-900 whitespace-pre-line">{lead.internalNote}</p>
                </div>
              )}
            </div>

            {/* Quick add note */}
            <div className="rounded-lg border border-border bg-sand-50 p-2">
              <FieldLabel>Add Note</FieldLabel>
              <Textarea rows={2} value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Type a quick admin note…" data-testid="crm-detail-note-input" />
              <div className="mt-2 flex justify-end">
                <Button size="sm" onClick={addNote} disabled={savingNote || !noteText.trim()} className="h-7 bg-clay text-xs text-white hover:bg-clay-dark" data-testid="crm-detail-note-save">Add Note</Button>
              </div>
            </div>

            {/* Notes timeline */}
            <div>
              <p className="text-[0.65rem] font-bold uppercase tracking-wide text-clay">Notes ({data?.notes?.length || 0})</p>
              <ul className="mt-1 space-y-1" data-testid="crm-detail-notes-list">
                {(data?.notes || []).map((n) => (
                  <li key={n.id} className="rounded border border-border bg-white px-2 py-1.5 text-[0.7rem]">
                    <p className="text-ink-900 whitespace-pre-line">{n.body}</p>
                    <p className="mt-0.5 text-[0.6rem] text-ink-700">{n.author} · {fmtTs(n.createdAt)}</p>
                  </li>
                ))}
                {(!data?.notes || data.notes.length === 0) && <li className="text-[0.7rem] italic text-ink-700">No notes yet.</li>}
              </ul>
            </div>

            {/* Activity timeline */}
            <div>
              <p className="text-[0.65rem] font-bold uppercase tracking-wide text-clay">Activity ({data?.activity?.length || 0})</p>
              <ul className="mt-1 space-y-1" data-testid="crm-detail-activity-list">
                {(data?.activity || []).slice(0, 12).map((a) => (
                  <li key={a.id} className="flex items-center gap-2 rounded bg-sand-50 px-2 py-1 text-[0.65rem]">
                    <ChevronRight className="h-3 w-3 text-clay" />
                    <span className="font-semibold text-clay">{a.action}</span>
                    <span className="flex-1 text-ink-700">{Object.keys(a.detail || {}).length > 0 ? JSON.stringify(a.detail).slice(0, 60) : ""}</span>
                    <span className="text-ink-700">{fmtTs(a.createdAt)}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <p className="text-[0.55rem] font-bold uppercase tracking-wide text-ink-700">{label}</p>
      <p className="text-[0.75rem] font-medium text-ink-900">{value || "—"}</p>
    </div>
  );
}

function fmtTs(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; }
}

// Build WhatsApp URL with the pre-filled support template (per user choice 3b).
function whatsappUrl(lead) {
  const num = (lead.whatsappNumber || lead.mobileNumber || "").replace(/\D/g, "");
  const phone = num.length === 10 ? `91${num}` : num;
  return `https://wa.me/${phone}?text=${encodeURIComponent(WHATSAPP_TEMPLATE)}`;
}

// =================================================================
// LEADS TABLE
// =================================================================
function LeadsTab({ catalog, summary, refreshSummary }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState({
    leadStatus: "", enquiryType: "", priority: "", workProgress: "", leadSource: "",
    followUpPending: false, completedOnly: false, positiveOnly: false, negativeOnly: false,
    sort: "latest",
  });
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState("add");
  const [formInitial, setFormInitial] = useState(null);
  const [detailLeadId, setDetailLeadId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = { q: q || undefined, ...filters };
      // strip empty / false flags so backend gets a clean query
      Object.keys(params).forEach((k) => { if (params[k] === "" || params[k] === false) delete params[k]; });
      const r = await axios.get(`${API}/admin/crm/leads`, { params });
      setRows(r.data.rows || []);
      setTotal(r.data.total || 0);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load leads"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [q, filters]);

  const openAdd = () => { setFormMode("add"); setFormInitial(null); setFormOpen(true); };
  const openEdit = (lead) => { setFormMode("edit"); setFormInitial(lead); setFormOpen(true); };

  const onDelete = async (lead) => {
    if (!confirm(`Delete lead "${lead.fullName}"? This also removes its notes & follow-ups.`)) return;
    try {
      await axios.delete(`${API}/admin/crm/leads/${lead.id}`);
      toast.success("Lead deleted");
      load(); refreshSummary?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const onComplete = async (lead) => {
    try {
      await axios.post(`${API}/admin/crm/leads/${lead.id}/complete`);
      toast.success("Marked completed");
      load(); refreshSummary?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not complete"); }
  };

  const exportCsv = () => {
    // Open the CSV stream in a new tab — backend forces download via
    // Content-Disposition. We rely on axios default Authorization header
    // already being set, but window.open won't carry it, so we POST a hidden form-like
    // approach: fetch with auth, blob it, save it.
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/crm/export/csv`, { responseType: "blob" });
        const url = URL.createObjectURL(r.data);
        const a = document.createElement("a");
        a.href = url; a.download = `crm_leads_${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
        toast.success("CSV downloaded");
      } catch (e) { toast.error("Export failed"); }
    })();
  };

  return (
    <div className="space-y-3">
      {/* Top bar: search, filter chips, actions */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative max-w-md flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-700" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name / mobile / school / district" className="pl-9" data-testid="crm-search" />
        </div>
        <SimpleSelect testId="crm-filter-status" value={filters.leadStatus} options={["", ...(catalog?.leadStatuses || [])]} onChange={(v) => setFilters({ ...filters, leadStatus: v })} />
        <SimpleSelect testId="crm-filter-priority" value={filters.priority} options={["", ...(catalog?.priorities || [])]} onChange={(v) => setFilters({ ...filters, priority: v })} />
        <SimpleSelect testId="crm-filter-enquiry" value={filters.enquiryType} options={["", ...(catalog?.enquiryTypes || [])]} onChange={(v) => setFilters({ ...filters, enquiryType: v })} />
        <Select value={filters.sort} onValueChange={(v) => setFilters({ ...filters, sort: v })}>
          <SelectTrigger className="h-8 w-36 text-xs" data-testid="crm-sort"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="latest">Latest first</SelectItem>
            <SelectItem value="oldest">Oldest first</SelectItem>
            <SelectItem value="priority">Priority first</SelectItem>
          </SelectContent>
        </Select>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <span className="text-[0.65rem] text-ink-700">{total} lead{total === 1 ? "" : "s"}</span>
          <Button size="sm" variant="outline" onClick={load} className="h-8 text-xs" data-testid="crm-refresh"><RefreshCw className="mr-1 h-3.5 w-3.5" /> Refresh</Button>
          <Button size="sm" variant="outline" onClick={exportCsv} className="h-8 text-xs" data-testid="crm-export-csv"><Download className="mr-1 h-3.5 w-3.5" /> Export CSV</Button>
          <Button size="sm" onClick={openAdd} className="h-8 bg-clay text-xs text-white hover:bg-clay-dark" data-testid="crm-add-lead-btn">
            <Plus className="mr-1 h-3.5 w-3.5" /> Add New Lead
          </Button>
        </div>
      </div>

      {/* Quick filter chips */}
      <div className="flex flex-wrap gap-1.5">
        <ChipToggle on={filters.followUpPending} onClick={() => setFilters({ ...filters, followUpPending: !filters.followUpPending })} testId="crm-chip-followup">Follow-Up Pending</ChipToggle>
        <ChipToggle on={filters.positiveOnly} onClick={() => setFilters({ ...filters, positiveOnly: !filters.positiveOnly, negativeOnly: false, completedOnly: false })} testId="crm-chip-positive">Positive only</ChipToggle>
        <ChipToggle on={filters.negativeOnly} onClick={() => setFilters({ ...filters, negativeOnly: !filters.negativeOnly, positiveOnly: false, completedOnly: false })} testId="crm-chip-negative">Negative only</ChipToggle>
        <ChipToggle on={filters.completedOnly} onClick={() => setFilters({ ...filters, completedOnly: !filters.completedOnly, positiveOnly: false, negativeOnly: false })} testId="crm-chip-completed">Completed only</ChipToggle>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-2xl border border-border bg-white shadow-sm">
        <table className="w-full text-xs" data-testid="crm-leads-table">
          <thead className="bg-sand-100 text-[0.6rem] uppercase tracking-wide text-ink-700">
            <tr>
              <th className="px-2 py-2 text-left">#</th>
              <th className="px-2 py-2 text-left">Name / Mobile</th>
              <th className="px-2 py-2 text-left">District</th>
              <th className="px-2 py-2 text-left">Enquiry</th>
              <th className="px-2 py-2 text-left">Status</th>
              <th className="px-2 py-2 text-left">Priority</th>
              <th className="px-2 py-2 text-left">Work</th>
              <th className="px-2 py-2 text-left">Follow-Up</th>
              <th className="px-2 py-2 text-left">Updated</th>
              <th className="px-2 py-2 text-left w-44">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map((r, i) => (
              <tr key={r.id} data-testid={`crm-row-${r.id}`} className="hover:bg-sand-50">
                <td className="px-2 py-1.5 text-ink-700">{i + 1}</td>
                <td className="px-2 py-1.5">
                  <p className="font-semibold text-ink-900">{r.fullName}</p>
                  <p className="text-[0.65rem] text-ink-700">{r.mobileNumber}</p>
                </td>
                <td className="px-2 py-1.5">
                  <p>{r.district || "—"}</p>
                  <p className="text-[0.65rem] text-ink-700">{r.schoolName || "—"}</p>
                </td>
                <td className="px-2 py-1.5">{r.enquiryType}</td>
                <td className="px-2 py-1.5"><Badge tone={STATUS_TONE[r.leadStatus]}>{r.leadStatus}</Badge></td>
                <td className="px-2 py-1.5"><Badge tone={PRIORITY_TONE[r.priority]}>{r.priority.replace(" Priority", "")}</Badge></td>
                <td className="px-2 py-1.5">{r.workProgress}</td>
                <td className="px-2 py-1.5 text-[0.65rem]">{r.followUpRequired ? `${r.followUpDate || ""} ${r.followUpTime || ""}` : "—"}</td>
                <td className="px-2 py-1.5 text-[0.65rem] text-ink-700">{fmtTs(r.updatedAt)}</td>
                <td className="px-2 py-1.5">
                  <div className="flex flex-wrap gap-1">
                    <IconBtn onClick={() => setDetailLeadId(r.id)} title="View" testId={`crm-row-${r.id}-view`}><Eye className="h-3.5 w-3.5" /></IconBtn>
                    <IconBtn onClick={() => openEdit(r)} title="Edit" testId={`crm-row-${r.id}-edit`}><Pencil className="h-3.5 w-3.5" /></IconBtn>
                    <IconBtn onClick={() => onComplete(r)} title="Mark complete" tone="emerald" testId={`crm-row-${r.id}-complete`}><CheckCircle2 className="h-3.5 w-3.5" /></IconBtn>
                    <a href={whatsappUrl(r)} target="_blank" rel="noreferrer" title="WhatsApp" data-testid={`crm-row-${r.id}-whatsapp`}><span className="inline-flex h-6 w-6 items-center justify-center rounded text-emerald-700 hover:bg-emerald-50"><MessageCircle className="h-3.5 w-3.5" /></span></a>
                    <a href={`tel:+91${(r.mobileNumber || "").replace(/\D/g, "")}`} title="Call" data-testid={`crm-row-${r.id}-call`}><span className="inline-flex h-6 w-6 items-center justify-center rounded text-sky-700 hover:bg-sky-50"><Phone className="h-3.5 w-3.5" /></span></a>
                    <IconBtn onClick={() => onDelete(r)} title="Delete" tone="rose" testId={`crm-row-${r.id}-delete`}><Trash2 className="h-3.5 w-3.5" /></IconBtn>
                  </div>
                </td>
              </tr>
            ))}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={10} className="px-3 py-6 text-center italic text-ink-700">No leads found. Click "Add New Lead" to capture your first enquiry.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <LeadFormDialog
        open={formOpen}
        mode={formMode}
        initial={formInitial}
        catalog={catalog}
        onClose={() => setFormOpen(false)}
        onSaved={() => { load(); refreshSummary?.(); }}
      />
      <LeadDetailDialog
        open={!!detailLeadId}
        leadId={detailLeadId}
        onClose={() => setDetailLeadId(null)}
        onChanged={() => { load(); refreshSummary?.(); }}
        onEdit={(lead) => { setDetailLeadId(null); openEdit(lead); }}
      />
    </div>
  );
}

function ChipToggle({ on, onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`rounded-full border px-2.5 py-0.5 text-[0.65rem] font-semibold transition ${on ? "border-clay bg-clay text-white" : "border-border bg-white text-ink-700 hover:bg-sand-100"}`}
    >
      <Filter className="mr-1 inline h-3 w-3" /> {children}
    </button>
  );
}

function IconBtn({ children, onClick, title, testId, tone }) {
  const map = { emerald: "text-emerald-700 hover:bg-emerald-50", rose: "text-rose-700 hover:bg-rose-50", default: "text-clay hover:bg-clay/10" };
  return (
    <button type="button" onClick={onClick} title={title} data-testid={testId} className={`inline-flex h-6 w-6 items-center justify-center rounded ${map[tone || "default"]}`}>{children}</button>
  );
}

// =================================================================
// ROOT — Dashboard + Leads tabs (Follow-ups & Export PDF in iter 2/3)
// =================================================================
export default function AdminCrm() {
  const [catalog, setCatalog] = useState(null);
  const [summary, setSummary] = useState(null);

  const refreshSummary = async () => {
    try {
      const r = await axios.get(`${API}/admin/crm/dashboard/summary`);
      setSummary(r.data);
    } catch (e) { /* silent */ }
  };

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/crm/catalog`);
        setCatalog(r.data);
      } catch (e) { toast.error("Could not load CRM catalog"); }
      await refreshSummary();
    })();
  }, []);

  return (
    <div className="space-y-4" data-testid="admin-crm-root">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-xl font-bold tracking-tight text-ink-900">CRM Lead Management</h2>
          <p className="text-xs text-ink-700">Track WhatsApp leads, enquiries, follow-ups and work completion · admin-only</p>
        </div>
        <Button size="sm" variant="outline" onClick={refreshSummary} className="h-8 text-xs" data-testid="crm-summary-refresh">
          <RefreshCw className="mr-1 h-3.5 w-3.5" /> Refresh Stats
        </Button>
      </header>

      <SummaryGrid summary={summary} />

      <Tabs defaultValue="leads" className="space-y-3">
        <TabsList className="bg-sand-100 p-1">
          <TabsTrigger value="leads" data-testid="crm-tab-leads" className="data-[state=active]:bg-clay data-[state=active]:text-white">
            <InboxIcon className="mr-1.5 h-3.5 w-3.5" /> Leads
          </TabsTrigger>
          <TabsTrigger value="followups" data-testid="crm-tab-followups" className="data-[state=active]:bg-clay data-[state=active]:text-white">
            <BellRing className="mr-1.5 h-3.5 w-3.5" /> Follow-Ups
          </TabsTrigger>
        </TabsList>

        <TabsContent value="leads">
          <LeadsTab catalog={catalog} summary={summary} refreshSummary={refreshSummary} />
        </TabsContent>

        <TabsContent value="followups">
          <FollowupsTab refreshSummary={refreshSummary} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// =================================================================
// FOLLOW-UPS tab — Today / Overdue / Upcoming buckets.
// =================================================================
function FollowupsTab({ refreshSummary }) {
  const [bucket, setBucket] = useState("today");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/crm/followups`, { params: { bucket } });
      setRows(r.data || []);
    } catch (e) { toast.error("Failed to load follow-ups"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [bucket]);

  const markDone = async (fu) => {
    try {
      await axios.post(`${API}/admin/crm/followups/${fu.id}/done`);
      toast.success("Follow-up marked done");
      load(); refreshSummary?.();
    } catch (e) { toast.error("Could not mark done"); }
  };

  const tone = bucket === "overdue" ? "border-rose-400 bg-rose-50" : bucket === "today" ? "border-amber-400 bg-amber-50" : "border-emerald-400 bg-emerald-50";

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        {[
          ["today", "Today", "amber"], ["overdue", "Overdue", "rose"], ["upcoming", "Upcoming", "emerald"], ["all", "All Pending", "clay"],
        ].map(([k, label]) => (
          <button
            key={k}
            type="button"
            onClick={() => setBucket(k)}
            data-testid={`crm-followup-bucket-${k}`}
            className={`rounded-full border px-3 py-1 text-xs font-semibold transition ${bucket === k ? "border-clay bg-clay text-white" : "border-border bg-white text-ink-700 hover:bg-sand-100"}`}
          >{label}</button>
        ))}
        <Button size="sm" variant="outline" onClick={load} className="ml-auto h-8 text-xs" data-testid="crm-followups-refresh"><RefreshCw className="mr-1 h-3.5 w-3.5" /> Refresh</Button>
      </div>

      <div className={`overflow-x-auto rounded-2xl border-2 ${tone}`}>
        <table className="w-full text-xs" data-testid="crm-followups-table">
          <thead className="bg-white/70 text-[0.6rem] uppercase tracking-wide text-ink-700">
            <tr>
              <th className="px-2 py-2 text-left">Date / Time</th>
              <th className="px-2 py-2 text-left">Name / Mobile</th>
              <th className="px-2 py-2 text-left">Status / Priority</th>
              <th className="px-2 py-2 text-left">Remark</th>
              <th className="px-2 py-2 text-left w-40">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map((r) => (
              <tr key={r.id} data-testid={`crm-followup-row-${r.id}`} className="bg-white/80">
                <td className="px-2 py-1.5 font-semibold">{r.date} {r.time && <span className="text-ink-700">· {r.time}</span>}</td>
                <td className="px-2 py-1.5">
                  <p className="font-medium">{r.fullName || "—"}</p>
                  <p className="text-[0.65rem] text-ink-700">{r.mobileNumber || "—"}</p>
                </td>
                <td className="px-2 py-1.5">
                  <div className="flex flex-wrap gap-1">
                    {r.leadStatus && <Badge tone={STATUS_TONE[r.leadStatus]}>{r.leadStatus}</Badge>}
                    {r.priority && <Badge tone={PRIORITY_TONE[r.priority]}>{r.priority.replace(" Priority", "")}</Badge>}
                  </div>
                </td>
                <td className="px-2 py-1.5 text-[0.7rem]">{r.remark || "—"}</td>
                <td className="px-2 py-1.5">
                  <div className="flex flex-wrap gap-1">
                    <Button size="sm" variant="outline" onClick={() => markDone(r)} className="h-6 border-emerald-400 text-[0.65rem] text-emerald-700 hover:bg-emerald-50" data-testid={`crm-followup-row-${r.id}-done`}>
                      <CheckCircle2 className="mr-1 h-3 w-3" /> Done
                    </Button>
                    <a href={`https://wa.me/91${(r.mobileNumber || "").replace(/\D/g, "")}?text=${encodeURIComponent(WHATSAPP_TEMPLATE)}`} target="_blank" rel="noreferrer" data-testid={`crm-followup-row-${r.id}-whatsapp`}>
                      <span className="inline-flex h-6 w-6 items-center justify-center rounded text-emerald-700 hover:bg-emerald-50"><MessageCircle className="h-3 w-3" /></span>
                    </a>
                    <a href={`tel:+91${(r.mobileNumber || "").replace(/\D/g, "")}`} data-testid={`crm-followup-row-${r.id}-call`}>
                      <span className="inline-flex h-6 w-6 items-center justify-center rounded text-sky-700 hover:bg-sky-50"><Phone className="h-3 w-3" /></span>
                    </a>
                  </div>
                </td>
              </tr>
            ))}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={5} className="px-3 py-6 text-center italic text-ink-700">No follow-ups in this bucket.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
